import { useState, useEffect, useCallback } from 'react';
import { deriveLocalPageFromGlobalPage } from '../utils';

interface UseDocumentPresenterProps {
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  btpActive: boolean;
  currentFileMetaData: any;
  recentSearches: Record<string, any>;
  setRecentSearches: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  caseId: string;
  startStopPresentHandlerWPresentView: any; // for now any
  switchDocument: (doc: any, goToPage?: number, forcePresent?: boolean) => void;
  docsToDisplay: any;
  requestIdRef?: React.MutableRefObject<number>;
  setDocsToDisplay: React.Dispatch<React.SetStateAction<any[]>>;
}
const globalPageRegex = /^[A-Za-z]+\d+(?:\.\d+)*$/;
const normalizeSlashInput = (raw: string) =>
  raw
    .replace(/\s*\/\s*/g, '/')
    .replace(/\/+$/, '')
    .trim();

const extractPrefixAndTab = (input: string) => {
  const parts = normalizeSlashInput(input).split('/');
  return {
    prefix: parts[0]?.toUpperCase() || '',
    tab: parts.length > 1 ? parts[1] : undefined,
  };
};

const pickBundleLocation = (doc: any, normalizedInput: string) => {
  if (!Array.isArray(doc?.bundleLocations)) return undefined;
  const { prefix, tab } = extractPrefixAndTab(normalizedInput);
  if (!prefix) return doc.bundleLocations[0];
  return (
    doc.bundleLocations.find((bl: any) => {
      const gp = (bl.globalPagePrefix || '').toUpperCase();
      const tb = bl.tab != null ? String(bl.tab) : undefined;
      const prefixMatch = gp === prefix;
      const tabMatch = tab ? tb === tab : true;
      return prefixMatch && tabMatch;
    }) || doc.bundleLocations[0]
  );
};
// Lock mechanism to prevent race conditions with localStorage operations
class StorageLockManager {
  private locks = new Map<string, Promise<void>>();

  async withLock<T>(key: string, operation: () => T | Promise<T>): Promise<T> {
    if (this.locks.has(key)) {
      await this.locks.get(key);
    }

    let resolve: () => void;
    const lockPromise = new Promise<void>((res) => {
      resolve = res;
    });
    this.locks.set(key, lockPromise);

    try {
      const result = await operation();
      return result;
    } finally {
      this.locks.delete(key);
      resolve!();
    }
  }
}

const storageLock = new StorageLockManager();

export type OpenOrigin = 'recent' | 'search' | 'internal';

// Clears search UI state and persisted search context in one awaited block to avoid races.

const createClearSearchStateFn = (
  caseId: string,
  requestIdRef: React.MutableRefObject<number> | undefined,
  setValue: React.Dispatch<React.SetStateAction<string>>,
  setDocsToDisplay: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  return async () => {
    setValue('');
    setDocsToDisplay([]);
    await atomicStorageOperations.clearSearchState(caseId);
    await atomicStorageOperations.persistDocsToDisplayWithContext(
      caseId,
      [],
      '',
      requestIdRef?.current,
    );
  };
};

export const safeSetJSON = (storage: Storage, key: string, value: any) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error('Failed to write', key, err);
    storage.removeItem(key); // keep cache clean
  }
};

export const safeGetJSON = (storage: Storage, key: string, defaultValue: any = null) => {
  try {
    const item = storage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (err) {
    console.error('Failed to read', key, err);
    return defaultValue;
  }
};

export const atomicStorageOperations = {
  updateRecentSearches: async (
    caseId: string,
    updateFn: (current: Record<string, any>) => Record<string, any>,
  ) => {
    return storageLock.withLock(`recentSearches_${caseId}`, () => {
      const current = safeGetJSON(localStorage, `${caseId}.recentSearches`, {});
      const updated = updateFn(current);
      safeSetJSON(localStorage, `${caseId}.recentSearches`, updated);
      return updated;
    });
  },

  updateCurrentlyPresenting: async (caseId: string, doc: any) => {
    return storageLock.withLock(`currentlyPresenting_${caseId}`, () => {
      safeSetJSON(sessionStorage, `${caseId}.currentlyPresenting`, doc);
      return doc;
    });
  },

  persistSearchValue: async (caseId: string, searchValue: string) => {
    return storageLock.withLock(`searchValue_${caseId}`, () => {
      safeSetJSON(sessionStorage, `${caseId}.searchValue`, searchValue);
    });
  },

  persistDocsToDisplay: async (caseId: string, docsToDisplay: any[]) => {
    return storageLock.withLock(`docsToDisplay_${caseId}`, () => {
      safeSetJSON(sessionStorage, `${caseId}.docsToDisplay`, docsToDisplay);
    });
  },

  persistDocsToDisplayWithContext: async (
    caseId: string,
    docsToDisplay: any[],
    searchValue: string,
    requestId?: number,
  ) => {
    return storageLock.withLock(`docsToDisplay_${caseId}`, () => {
      const dataWithContext = {
        docs: docsToDisplay,
        searchValue,
        requestId,
        timestamp: Date.now(),
      };
      safeSetJSON(sessionStorage, `${caseId}.docsToDisplay`, dataWithContext);
    });
  },

  clearSearchState: async (caseId: string) => {
    return storageLock.withLock(`searchState_${caseId}`, () => {
      safeSetJSON(sessionStorage, `${caseId}.searchValue`, '');
      safeSetJSON(sessionStorage, `${caseId}.docsToDisplay`, []);
    });
  },
};

export function usePresentView({
  value,
  setValue,
  btpActive,
  currentFileMetaData,
  recentSearches,
  setRecentSearches,
  caseId,
  startStopPresentHandlerWPresentView,
  switchDocument,
  docsToDisplay,
  requestIdRef,
  setDocsToDisplay,
}: UseDocumentPresenterProps) {
  const [chipPageNumber, setChipPageNumber] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState(() =>
    safeGetJSON(sessionStorage, `${caseId}.currentlyPresenting`, ''),
  );
  const isPublicPresent = new URLSearchParams(window.location.search).get('present') === 'public';

  const clearSearchStateSafely = useCallback(
    createClearSearchStateFn(caseId, requestIdRef, setValue, setDocsToDisplay),
    [caseId, requestIdRef, setValue, setDocsToDisplay],
  );

  const parseSearchPage = useCallback((inputValue: string): string => {
    const sanitizedInput = normalizeSlashInput(inputValue);
    const ticketMatch = sanitizedInput.match(/^([A-Za-z]+-\d+-\d+)\/(\d+)$/); // e.g., "TICKET-123-456/1"
    if (ticketMatch) {
      return ticketMatch[2];
    }

    // Plain digits alone are treated as a document lookup, so default to page 1
    if (/^\d+$/.test(sanitizedInput)) {
      return '1';
    }
    if (sanitizedInput.includes('/')) {
      const parts = sanitizedInput.split('/');
      if (/^[\d-]+$/i.test(parts[0])) {
        return parts[parts.length - 1];
      }
      if (parts.length === 2) {
        return '1';
      }
      return parts[parts.length - 1];
    }
    if (sanitizedInput === '') return '1';

    if (/^[A-Za-z]+\d+(?:\.\d+)*$/.test(sanitizedInput)) {
      return sanitizedInput;
    }

    if (/^[A-Za-z]$/.test(sanitizedInput)) {
      return '1';
    }
    return '1';
  }, []);

  const getDisplayPage = useCallback(
    (searchPage: any, inputValue) => {
      const sanitizedInput = normalizeSlashInput(inputValue);
      if (sanitizedInput.includes('/')) {
        const first = sanitizedInput.split('/')[0];
        if (/^[\d-]+$/i.test(first)) {
          return searchPage?.toUpperCase();
        }
        return sanitizedInput?.toUpperCase();
      }
      return searchPage?.toUpperCase();
    },
    [value],
  );

  const handleOpenDoc = useCallback(
    async (doc: any, viewerControlRef?: any, manualPageValue?: string) => {
      const originalInput = manualPageValue || value || '';
      const normalizedInput = normalizeSlashInput(originalInput);
      const shouldCacheSearch = normalizedInput.length > 0;
      const searchPage = parseSearchPage(normalizedInput);
      setChipPageNumber(searchPage);
      let pageNum = 1;
      if (/^\d+$/.test(searchPage)) {
        // If the parsed searchPage is purely numeric, use it as the page number
        pageNum = parseInt(searchPage, 10);
      }
      // Otherwise, if using global-page format and global paging is inactive, derive local page
      else if (globalPageRegex.test(normalizedInput) && btpActive === false) {
        pageNum = deriveLocalPageFromGlobalPage(searchPage.toUpperCase(), doc, btpActive);
      }
      // Otherwise, for bundle/tab/page (BTP) entries, parse the numeric page part
      else if (normalizedInput.includes('/') && btpActive === false) {
        const parts = normalizedInput.split('/');
        const lastPart = parts[parts.length - 1];
        pageNum = /^\d+$/.test(lastPart) ? parseInt(lastPart, 10) : 1;
      }

      const pickedLoc = pickBundleLocation(doc, normalizedInput);
      const bundleLoc = pickedLoc || {};
      const globalPagePrefix = (
        doc.globalPagePrefix ||
        bundleLoc?.globalPagePrefix ||
        ''
      )?.toUpperCase();
      const docWithPage = {
        ...doc,
        bundleLocations: doc.bundleLocations,
        searchPage,
        startPage: doc.startPage ?? bundleLoc.startPage ?? '1',
        pageCount: doc.pageCount ?? bundleLoc.pageCount,
        tab: doc.tab ?? bundleLoc.tab ?? '1',
        globalPagePrefix,
        timestamp: Date.now(),
        name: doc.name,
        localPageNumber: pageNum,
        folderId: doc.folderId ?? bundleLoc.folderId,
        goToPage: pageNum,
        displayPage: getDisplayPage(searchPage, normalizedInput),
        btpParameter: btpActive,
        originalInput: normalizedInput,
      };

      const sameId = doc.id === currentFileMetaData.id;
      const prevPrefix = (currentFileMetaData.globalPagePrefix || '').toUpperCase();
      const prevTab = String(currentFileMetaData.tab ?? '');
      const newPrefix = globalPagePrefix;
      const newTab = String(docWithPage.tab ?? '');
      const sameContext = sameId && prevPrefix === newPrefix && prevTab === newTab;

      if (sameContext) {
        if (shouldCacheSearch) {
          const docKey = `${doc.id}_${normalizedInput.toUpperCase()}_${pageNum}_${globalPagePrefix}_${doc.folderId}`;

          try {
            const updatedRecentSearches = await atomicStorageOperations.updateRecentSearches(
              caseId,
              (current) => ({ ...current, [docKey]: docWithPage }),
            );
            setRecentSearches(updatedRecentSearches);
          } catch (error) {
            console.error('Failed to update recent searches:', error);
          }
        }

        try {
          await atomicStorageOperations.updateCurrentlyPresenting(caseId, docWithPage);
        } catch (error) {
          console.error('Failed to update currently presenting:', error);
        }

        viewerControlRef.current.setPageNumber(pageNum);
        startStopPresentHandlerWPresentView('public', true, docWithPage, pageNum);
        return;
      }

      if (shouldCacheSearch) {
        const docKey = `${doc.id}_${normalizedInput.toUpperCase()}_${pageNum}_${globalPagePrefix}_${doc.folderId}`;

        try {
          const updatedRecentSearches = await atomicStorageOperations.updateRecentSearches(
            caseId,
            (current) => {
              if (!current[docKey]) {
                return { ...current, [docKey]: docWithPage };
              }
              return current;
            },
          );
          setRecentSearches(updatedRecentSearches);
        } catch (error) {
          console.error('Failed to update recent searches:', error);
        }
      }

      setSelectedDocument(docWithPage);

      try {
        await atomicStorageOperations.updateCurrentlyPresenting(caseId, docWithPage);
      } catch (error) {
        console.error('Failed to update currently presenting:', error);
      }

      try {
        if (docsToDisplay && docsToDisplay.length > 0 && normalizedInput.trim()) {
          await atomicStorageOperations.persistDocsToDisplayWithContext(
            caseId,
            docsToDisplay,
            normalizedInput,
            requestIdRef?.current,
          );
        }
      } catch (error) {
        console.error('Failed to persist search state before document switch:', error);
      }

      switchDocument(docWithPage, pageNum, true);
    },
    [
      parseSearchPage,
      value,
      btpActive,
      getDisplayPage,
      currentFileMetaData.id,
      currentFileMetaData.globalPagePrefix,
      currentFileMetaData.tab,
      recentSearches,
      setRecentSearches,
      caseId,
      switchDocument,
      startStopPresentHandlerWPresentView,
      docsToDisplay,
      setDocsToDisplay,
    ],
  );

  const handleOpenRecentlyPresented = useCallback(
    async (
      doc: any,
      viewerControlRef: any,
      forcePresent?: boolean,
      calledFromHandleOpenDocOrOrigin?: OpenOrigin,
    ) => {
      const origin: OpenOrigin =
        typeof calledFromHandleOpenDocOrOrigin === 'string'
          ? calledFromHandleOpenDocOrOrigin
          : calledFromHandleOpenDocOrOrigin
            ? 'search'
            : 'recent';

      const spObj =
        typeof doc.searchPage === 'object' && doc.searchPage?.searchPage
          ? doc.searchPage
          : { searchPage: String(doc.searchPage), btpParameter: false };
      const searchPageStr = spObj.searchPage;
      setChipPageNumber(searchPageStr);

      let pageNum = 1;
      if (/^\d+$/.test(searchPageStr)) {
        pageNum = parseInt(searchPageStr, 10);
      } else if (globalPageRegex.test(searchPageStr) && btpActive === false) {
        pageNum = deriveLocalPageFromGlobalPage(searchPageStr.toUpperCase(), doc);
      }
      const pickedLocRP = pickBundleLocation(doc, searchPageStr);
      const globalPagePrefix =
        doc.globalPagePrefix ||
        (pickedLocRP && pickedLocRP.globalPagePrefix) ||
        (doc.bundleLocations && doc.bundleLocations[0] && doc.bundleLocations[0].globalPagePrefix);
      const localPageNumber = doc.localPageNumber || pageNum;

      const updatedDoc = {
        ...doc,
        timestamp: Date.now(),
      };
      const prevPrefixRP = (currentFileMetaData.globalPagePrefix || '').toUpperCase();
      const prevTabRP = String(currentFileMetaData.tab ?? '');
      const newPrefixRP = (globalPagePrefix || '').toUpperCase();
      const newTabRP = String(doc.tab ?? pickedLocRP?.tab ?? '');
      const contextChanged =
        doc.id !== currentFileMetaData.id || prevPrefixRP !== newPrefixRP || prevTabRP !== newTabRP;

      if (origin === 'recent' && contextChanged) {
        try {
          await clearSearchStateSafely();
        } catch (error) {
          console.error('Failed to clear search state:', error);
        }
      }

      switchDocument(updatedDoc, localPageNumber, forcePresent);

      const originalInput = doc.originalInput || searchPageStr || '';
      const normalizedInput = normalizeSlashInput(originalInput);
      const docKey = `${doc.id}_${normalizedInput.toUpperCase()}_${pageNum}_${globalPagePrefix}_${doc.folderId}`;
      const docToStore = isPublicPresent ? updatedDoc : doc;

      try {
        const updatedRecentSearches = await atomicStorageOperations.updateRecentSearches(
          caseId,
          (current) => ({ ...current, [docKey]: docToStore }),
        );
        setRecentSearches(updatedRecentSearches);
      } catch (error) {
        console.error('Failed to update recent searches:', error);
      }

      try {
        await atomicStorageOperations.updateCurrentlyPresenting(caseId, docToStore);
      } catch (error) {
        console.error('Failed to update currently presenting:', error);
      }

      if (doc.id === currentFileMetaData.id) {
        viewerControlRef.current.setPageNumber(pageNum);
        return;
      }
    },
    [
      btpActive,
      caseId,
      currentFileMetaData.id,
      currentFileMetaData.globalPagePrefix,
      currentFileMetaData.tab,
      switchDocument,
      setRecentSearches,
      clearSearchStateSafely,
    ],
  );

  useEffect(() => {
    let searchPage = '';
    if (docsToDisplay.length === 0) {
      searchPage = parseSearchPage(value);
    } else {
      searchPage = parseSearchPage(value);
    }

    setChipPageNumber(searchPage);
  }, [currentFileMetaData, docsToDisplay, parseSearchPage, value]);

  // Only persist sessionStorage on explicit actions, not automatic state changes
  // This prevents race conditions where stale sessionStorage overwrites fresh API results

  return {
    chipPageNumber,
    handleOpenDoc,
    handleOpenRecentlyPresented,
    parseSearchPage,
    selectedDocument,
  };
}
